'use client';

import { assertNever } from '@/globals/helpers/assert.helpers';
import { onDOPRouterEvent } from '@/globals/routing/useDOPRouterEvent';
import React, {
	createContext,
	useContext,
	ReactNode,
	useEffect,
	useReducer,
} from 'react';

type OpenedMenu = {
	mainMenu: boolean;
	submenu: string | undefined;
	menuType: ResponsiveMenuType | undefined;
};
export type ResponsiveMenuType = 'hamburger' | 'navBar';
type MenuAction =
	| {
			type: 'closeSubmenu';
			submenuID: string;
			menuType: ResponsiveMenuType;
	  }
	| {
			type: 'openSubmenu';
			submenuID: string;
			menuType: ResponsiveMenuType;
	  }
	| { type: 'openMainMenu' }
	| { type: 'closeMainMenu' }
	| { type: 'menuType'; menuType: ResponsiveMenuType };

const MainMenuContext = createContext<
	| [openedMenu: OpenedMenu, dispatchOpenedMenu: React.Dispatch<MenuAction>]
	| undefined
>(undefined);

export const useMainMenuOpened = () => {
	const context = useContext(MainMenuContext);
	if (context == null) {
		throw new Error('useMainMenuOpened must be used within a MainMenuProvider');
	}
	return context;
};

const openMainMenuFromNavBar = ({
	mainMenuOpened,
	subMenuOpened,
	menuType,
}: {
	mainMenuOpened: boolean;
	subMenuOpened: boolean;
	menuType: ResponsiveMenuType;
}) => {
	if (menuType === 'hamburger') {
		return mainMenuOpened;
	}

	return subMenuOpened;
};

const reducer = (openedMainMenu: OpenedMenu, action: MenuAction) => {
	switch (action.type) {
		case 'openSubmenu': {
			return {
				submenu: action.submenuID,
				mainMenu: openMainMenuFromNavBar({
					mainMenuOpened: openedMainMenu.mainMenu,
					subMenuOpened: true,
					menuType: action.menuType,
				}),
				menuType: openedMainMenu.menuType,
			};
		}
		case 'closeSubmenu': {
			return {
				submenu: undefined,
				mainMenu: openMainMenuFromNavBar({
					mainMenuOpened: openedMainMenu.mainMenu,
					subMenuOpened: false,
					menuType: action.menuType,
				}),
				menuType: openedMainMenu.menuType,
			};
		}
		case 'openMainMenu': {
			return {
				submenu: openedMainMenu.submenu,
				mainMenu: true,
				menuType: openedMainMenu.menuType,
			};
		}
		case 'closeMainMenu': {
			return {
				submenu: undefined,
				mainMenu: false,
				menuType: openedMainMenu.menuType,
			};
		}
		case 'menuType': {
			return {
				submenu: openedMainMenu.submenu,
				mainMenu: openedMainMenu.mainMenu,
				menuType: action.menuType,
			};
		}
		default:
			return assertNever(action);
	}
};

export const MainMenuProvider = ({ children }: { children: ReactNode }) => {
	const [openedMainMenu, dispatchOpenedMainMenu] = useReducer(reducer, {
		mainMenu: false,
		submenu: undefined,
		menuType: undefined,
	});

	// Close on navigate
	useEffect(() => {
		return onDOPRouterEvent(() => {
			dispatchOpenedMainMenu({ type: 'closeMainMenu' });
		});
	}, []);

	return (
		<MainMenuContext.Provider value={[openedMainMenu, dispatchOpenedMainMenu]}>
			{children}
		</MainMenuContext.Provider>
	);
};
